<script setup lang="ts">
import {
  ref,
  computed,
  useTemplateRef,
  onMounted,
  onUnmounted,
  watch,
} from "vue";
import type { TemplateRef } from "../../..";

type Rect = {
  width: number;
  height: number;
  bottom: number;
  right: number;
  left: number;
  top: number;
};
type HorizontalPosition = number | "left" | "center" | "right";
type VerticalPosition = number | "top" | "center" | "bottom";
type PositionArg = [
  HorizontalPosition | ((rects: { anchor: Rect; self: Rect }) => number),
  VerticalPosition | ((rects: { anchor: Rect; self: Rect }) => number)
];

const {
  anchor,
  positionOnAnchor = ["right", "bottom"],
  positionSelf = ["right", "top"],
  windowEdgePadding = 2,
} = defineProps<{
  windowEdgePadding?: number;
  anchor: TemplateRef | null;
  positionOnAnchor?: PositionArg;
  positionSelf?: PositionArg;
  containToWindow?: boolean;
}>();

const visible = defineModel("visible", { default: false });

const anchorElement = computed(() =>
  anchor !== null ? ("$el" in anchor ? anchor.$el : anchor) : null
);
const contentContainer = useTemplateRef("contentContainer");

const rectSelf = ref<Rect>();
const rectAnchor = ref<Rect>();

const translate3d = computed(() => {
  if (!rectSelf.value || !rectAnchor.value) return `translate3d(0px,0px,0px)`;

  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;

  const getHorizontal = (position: HorizontalPosition, rect: Rect) =>
    position === "left"
      ? rect.left
      : position === "center"
      ? rect.left + rect.width / 2
      : position === "right"
      ? rect.left + rect.width
      : position;

  const getVertical = (position: VerticalPosition, rect: Rect) =>
    position === "top"
      ? rect.top
      : position === "center"
      ? rect.top + rect.height / 2
      : position === "bottom"
      ? rect.top + rect.height
      : position;

  const rects = { anchor: rectAnchor.value, self: rectSelf.value };

  const anchorHorizontal =
    typeof positionOnAnchor[0] === "function"
      ? positionOnAnchor[0](rects)
      : getHorizontal(positionOnAnchor[0], rectAnchor.value);

  const anchorVertical =
    typeof positionOnAnchor[1] === "function"
      ? positionOnAnchor[1](rects)
      : getVertical(positionOnAnchor[1], rectAnchor.value);

  const selfHorizontal =
    typeof positionSelf[0] === "function"
      ? -positionSelf[0](rects)
      : getHorizontal(positionSelf[0], rectSelf.value);

  const selfVertical =
    typeof positionSelf[1] === "function"
      ? -positionSelf[1](rects)
      : getVertical(positionSelf[1], rectSelf.value);

  const position = [
    anchorHorizontal - selfHorizontal,
    anchorVertical - selfVertical,
  ];

  const clampedX = Math.max(
    windowEdgePadding,
    Math.min(
      position[0],
      screenWidth - rectSelf.value.width - windowEdgePadding
    )
  );
  const clampedY = Math.max(
    windowEdgePadding,
    Math.min(
      position[1],
      screenHeight - rectSelf.value.height - windowEdgePadding
    )
  );

  return `translate3d(${clampedX}px,${clampedY}px,0px)`;
});

const resizeObserver = ref<ResizeObserver>();

const onScroll = (e: Event) => {
  if (
    contentContainer.value !== e.target &&
    !contentContainer.value?.contains(e.target as HTMLElement)
  )
    visible.value = false;
};

const rectFromDomRect = (rect: DOMRect): Rect => ({
  width: rect.width,
  height: rect.height,
  left: rect.left,
  top: rect.top,
  bottom: rect.bottom,
  right: rect.right,
});

const listen = () => {
  if (!contentContainer.value || !anchorElement.value) return;

  if (!resizeObserver.value)
    resizeObserver.value = new ResizeObserver((resizeEntries) => {
      for (let entry of resizeEntries) {
        switch (entry.target) {
          case contentContainer.value:
            if (entry.contentRect.width && entry.contentRect.height)
              rectSelf.value = entry.contentRect;
            break;
          case anchorElement.value:
            rectAnchor.value = rectFromDomRect(
              anchorElement.value!.getBoundingClientRect()
            );
            break;
        }
      }
    });

  resizeObserver.value.observe(contentContainer.value);
  resizeObserver.value.observe(anchorElement.value);
  window.addEventListener("scroll", onScroll, true);
};

const unlisten = () => {
  if (contentContainer.value)
    resizeObserver.value?.unobserve(contentContainer.value);
  if (anchorElement.value) resizeObserver.value?.unobserve(anchorElement.value);

  resizeObserver.value?.disconnect();
  resizeObserver.value = undefined;
  window.removeEventListener("scroll", onScroll);
};

onMounted(() => {
  listen();
});

onUnmounted(() => {
  unlisten();
});

const show = async () => {
  if (!anchorElement.value) return;

  const rect = anchorElement.value.getBoundingClientRect();
  rectAnchor.value = rectFromDomRect(rect);

  requestAnimationFrame(() => contentContainer.value?.showPopover());
};

const hide = () => {
  rectAnchor.value = undefined;
  rectSelf.value = undefined;

  contentContainer.value?.hidePopover();
};

const handleToggle = (e: ToggleEvent) => {
  if (e.newState === "open") {
    visible.value = true;
    listen();
  } else if (e.newState === "closed") {
    visible.value = false;
    unlisten();
  }
};

defineExpose({
  show,
  hide,
});

watch(visible, (v) => (v ? show() : hide()), { immediate: true });
</script>
<template>
  <div
    ref="contentContainer"
    :class="$style.content"
    :style="{
      transform: translate3d,
      visibility: rectSelf && rectAnchor ? 'visible' : 'hidden',
    }"
    @toggle="handleToggle"
    popover
  >
    <slot />
  </div>
</template>

<style module>
.content {
  background-color: var(--color-transparent);
  will-change: transform;
  margin: 0;
  padding: 0;
  border: none;
  overflow: visible;
}
</style>
